import { ArrowBackIos } from '@mui/icons-material';
import { Box, Button, Stack, Step, StepContent, StepLabel, Stepper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Page, PageTitle } from 'src/components';
import { StepExam, StepPoints, StepProperties } from 'src/containers';
import { CREATE_STEP_STEPS, STEP_CONTENT_TYPE } from 'src/helpers';
import { createStep, errorHandler, updateStep } from 'src/services';
import { findPersianDefinition } from 'src/utils/findPersianOrder';

const useForceUpdate = () => {
  // eslint-disable-next-line no-unused-vars
  const [value, setValue] = useState();
  return () => setValue((value) => value + 1);
};

export default function CreateStep({ onClose, cb, selectedStep, courseProperty }) {
  const { state } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const courseID = useSelector((state) => state.course.courseID);
  const forceUpdate = useForceUpdate();

  const [stepHasMediaFile, setStepHasMediaFile] = useState(false);
  const [active, setActive] = useState(1);
  const [properties, setProperties] = useState({
    title: '',
    stepTime: '',
    type: {},
    content: '',
    html: '',
    slideHashes: [],
    uploadManually: false,
    description: '',
    manual: {
      courseID,
      size: 0,
      videoName: '',
      videoUrl: '',
      mimeType: 'video/mp4',
    },
    medias: [],
  });
  const [points, setPoints] = useState({
    reward: 0,
    required: 0,
    examLength: 0,
  });
  const [exam, setExam] = useState([]);
  const [hasExam, setHasExam] = useState(false);
  const [examLength, setExamLength] = useState(0);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const onSubmit = async () => {
    const hasNotAnsweredOption = exam.map(({ answer }) => answer === '').includes(true);
    const hasEmptyOption = exam.map(({ options }) => options.some((option) => option === '')).includes(true);
    const data = {
      properties: { ...properties, stepTime: properties.stepTime || 0 },
      points,
      exam,
      examLength,
      courseID,
    };

    if (hasNotAnsweredOption) {
      enqueueSnackbar('لطفا پاسخ سوال را مشخص کنید', { variant: 'info' });
      return;
    }
    if (hasEmptyOption) {
      enqueueSnackbar('لطفا همه گزینه های خالی را پر کنید', { variant: 'info' });
      return;
    }
    if (hasExam && points.examLength > exam.length) {
      enqueueSnackbar('تعداد سوالات  در آزمون باید کمتر از تعداد سوالات مطرح شده باشد', { variant: 'info' });
      return;
    }
    setLoading(true);
    try {
      if (selectedStep._id) {
        await updateStep(data, selectedStep._id);
      } else {
        await createStep(data);
      }
      setLoading(false);
      forceUpdate();
      cb();
      onClose();
    } catch (error) {
      errorHandler(error?.response?.data.message);
      enqueueSnackbar('خطا در ایجاد گام', { variant: 'error' });
      setLoading(false);
    }
  };
  const nextStep = (step) => {
    switch (step) {
      case 1:
        if (
          properties.title === '' ||
          (stepHasMediaFile && properties.medias.length === 0) ||
          properties.type.value === undefined ||
          (properties.type.value === 0 && properties.slideHashes.length === 0) ||
          (properties.type.value === 1 && properties.content === '') ||
          (properties.type.value === 1 && Array.isArray(properties.content)) ||
          (properties.type.value === 2 && properties.content === '') ||
          (properties.type.value === 2 && Array.isArray(properties.content)) ||
          (properties.type.value === 3 && properties.html === '') ||
          (properties.type.value === 3 && properties.html === '<p><br></p>')
        ) {
          setError(true);
          return;
        }
        setActive(2);
        break;
      case 2:
        if (points.required === 0 || points.reward === 0) {
          setError(true);
          return;
        }
        setActive(3);
        break;
      case 3:
        if (hasExam && exam.length === 0) {
          setError(true);
          return;
        }
        if ((hasExam && examLength === 0) || (hasExam && examLength > exam.length)) {
          setError(true);
          enqueueSnackbar('تعداد سوالات مطرح شده در آزمون نباید بیشتر از تعداد کل سوالات و یا صفر باشد', {
            variant: 'info',
          });
          return;
        }
        onSubmit();
        break;
      default:
        break;
    }
  };
  const prevStep = (step) => {
    setActive(step - 1);
  };
  const renderStepContent = (step) => {
    switch (step) {
      case 1:
        return (
          <StepProperties
            properties={properties}
            setProperties={setProperties}
            error={error}
            stepHasMediaFile={stepHasMediaFile}
            setStepHasMediaFile={setStepHasMediaFile}
          />
        );
      case 2:
        return <StepPoints points={points} setPoints={setPoints} error={error} />;
      case 3:
        return (
          <StepExam
            exam={exam}
            setExam={setExam}
            hasExam={hasExam}
            setHasExam={setHasExam}
            error={error}
            examLength={examLength}
            setExamLength={setExamLength}
          />
        );
      default:
        break;
    }
  };
  const renderButtons = (step) => {
    switch (step) {
      case 1:
        return (
          <Button variant="contained" onClick={() => nextStep(step)} sx={{ float: 'right', my: 3 }}>
            بعدی
          </Button>
        );
      case 2:
        return (
          <Stack flexDirection="row" sx={{ float: 'right', my: 3 }}>
            <Button variant="outlined" onClick={() => prevStep(step)} sx={{ mr: 1 }}>
              قبلی
            </Button>
            <Button variant="contained" onClick={() => nextStep(step)}>
              بعدی
            </Button>
          </Stack>
        );
      case 3:
        return (
          <Stack flexDirection="row" sx={{ float: 'right', my: 3 }}>
            <Button variant="outlined" onClick={() => prevStep(step)} sx={{ mr: 1 }} disabled={loading}>
              قبلی
            </Button>
            <Button variant="contained" onClick={() => nextStep(3)} disabled={loading}>
              ثبت گام
            </Button>
          </Stack>
        );

      default:
        return (
          <Button variant="contained" onClick={() => setActive(1)}>
            گام اول
          </Button>
        );
    }
  };

  useEffect(() => {
    if (selectedStep._id === undefined) return;
    setProperties({
      ...properties,
      title: selectedStep?.title,
      type: STEP_CONTENT_TYPE.find((t) => t.value === selectedStep?.type),
      slideHashes: selectedStep?.slideHashes,
      content: selectedStep?.mediaHash,
      description: selectedStep?.mediaDescription,
      stepTime: selectedStep?.stepTime,
      html: selectedStep?.html,
      medias: selectedStep?.medias,
    });
    setPoints({ required: selectedStep?.passScore, reward: selectedStep?.score });
    selectedStep?.questions?.length > 0 ? setHasExam(true) : setHasExam(false);
    setExam(selectedStep?.questions);
    setExamLength(selectedStep?.examLength);

    if (selectedStep?.medias?.length > 0) {
      setStepHasMediaFile(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStep]);

  return (
    <Page title="ایجاد گام">
      <PageTitle title="ایجاد گام" />
      <Stack flexDirection="row">
        <PageTitle
          title={`${state?.course?.title || courseProperty?.title || '-'}  |  گام ${findPersianDefinition(
            state?.currentStep || 1
          )}`}
          size={18}
        />

        <Box sx={{ flexGrow: 1 }} />

        <Button
          endIcon={<ArrowBackIos />}
          onClick={() => {
            onClose();
          }}
        >
          بازگشت
        </Button>
      </Stack>
      <Stepper activeStep={active} orientation="vertical">
        {CREATE_STEP_STEPS.map((step, index) => {
          const { label, value } = step;
          return (
            <Step key={index} active={active === value}>
              <StepLabel>{label}</StepLabel>
              <StepContent>{renderStepContent(active)}</StepContent>
              <StepContent>{active === value && renderButtons(active)}</StepContent>
            </Step>
          );
        })}
      </Stepper>
    </Page>
  );
}
